import React, { useState, useEffect } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Filler } from 'chart.js';
import { Doughnut, Line } from 'react-chartjs-2';
import StatCard from './components/StatCard';
import DonutPanel from './components/DonutPanel';
import KPIPanel from './components/KPIPanel';
import AreaPanel from './components/AreaPanel';
import './App.css';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Filler);

const tooltipConfig = {
  backgroundColor: 'rgba(255,255,255,0.97)',
  borderColor: 'rgba(0,0,0,0.08)',
  borderWidth: 1,
  titleColor: '#1c2b23',
  bodyColor: '#6b7280',
  titleFont: { family: "'Plus Jakarta Sans', sans-serif", weight: '800', size: 11 },
  bodyFont: { family: "'Plus Jakarta Sans', sans-serif", size: 11 },
  padding: 10,
  cornerRadius: 10,
  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
};

const App = () => {
  const [currentTime, setCurrentTime] = React.useState(new Date().toLocaleTimeString('en-US', { hour12: false }));
  const [activeNav, setActiveNav] = React.useState('Overview');
  const [energyValue, setEnergyValue] = React.useState(0);

  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date().toLocaleTimeString('en-US', { hour12: false }));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  React.useEffect(() => {
    const duration = 1100;
    const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      setEnergyValue(Math.round(eased * 6350));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, []);

  const navItems = ['Overview', 'Energy', 'Water', 'Fuel', 'Waste', 'Reports'];

  const energyDonutData = {
    labels: ['Power', 'Solar', 'Wind', 'Grid'],
    datasets: [{
      data: [49.4, 10, 4, 36.6],
      backgroundColor: ['#22c55e', '#fbbf24', '#f472b6', '#60a5fa'],
      borderColor: '#f0fdf4',
      borderWidth: 3,
      hoverOffset: 6,
    }],
  };

  const waterDonutData = {
    labels: ['Generated', 'Raw', 'Soft', 'RO'],
    datasets: [{
      data: [53.7, 25.8, 7.6, 12.9],
      backgroundColor: ['#3b82f6', '#06b6d4', '#10b981', '#8b5cf6'],
      borderColor: '#eff6ff',
      borderWidth: 3,
      hoverOffset: 6,
    }],
  };

  const wasteDonutData = {
    labels: ['Wood', 'Plastic', 'Cardboard'],
    datasets: [{
      data: [41, 24, 35],
      backgroundColor: ['#10b981', '#ef4444', '#06b6d4'],
      borderColor: '#fff1f2',
      borderWidth: 3,
      hoverOffset: 6,
    }],
  };

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
  const consumptionData = [18252, 10237, 18878, 17878, 18857, 18962, 18278, 17492, 14969, 17810, 18236, 18022, 19574, 12659, 5378, 0, 3585, 6544];
  const generationData = [2981, 19686, 18322, 19795, 19371, 11527, 13312, 5098, 20388, 20369, 19391, 19534, 19192, 19864, 20865, 19540, 14714, null];

  const areaChartData = {
    labels: months,
    datasets: [
      {
        label: 'Consumption',
        data: consumptionData,
        fill: true,
        backgroundColor: 'rgba(34,197,94,0.08)',
        borderColor: '#22c55e',
        borderWidth: 2.5,
        pointRadius: 3,
        pointBackgroundColor: '#22c55e',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        tension: 0.42,
      },
      {
        label: 'Generation',
        data: generationData,
        fill: true,
        backgroundColor: 'rgba(59,130,246,0.07)',
        borderColor: '#3b82f6',
        borderWidth: 2.5,
        pointRadius: 3,
        pointBackgroundColor: '#3b82f6',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        tension: 0.42,
      },
    ],
  };

  const areaChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: tooltipConfig },
    scales: {
      x: {
        ticks: { color: '#9ca3af', font: { family: "'Plus Jakarta Sans', sans-serif", size: 10 } },
        grid: { color: 'rgba(0,0,0,0.04)' },
        border: { color: 'rgba(0,0,0,0.06)' },
      },
      y: {
        ticks: { color: '#9ca3af', font: { family: "'Plus Jakarta Sans', sans-serif", size: 10 } },
        grid: { color: 'rgba(0,0,0,0.04)' },
        border: { color: 'rgba(0,0,0,0.06)' },
      },
    },
    animation: { duration: 1400, easing: 'easeOutQuart' },
  };

  const donutOptions = {
    responsive: true,
    maintainAspectRatio: true,
    cutout: '72%',
    plugins: { legend: { display: false }, tooltip: tooltipConfig },
    animation: { duration: 1400, easing: 'easeOutQuart' },
  };

  return (
    <>
      <Topbar currentTime={currentTime} navItems={navItems} activeNav={activeNav} setActiveNav={setActiveNav} />
      <div className="grid">
        <StatCard
          icon="⚡"
          value={energyValue.toLocaleString()}
          label="MWh · Energy Consumed"
          tag="↑ 4.2%"
          fillWidth="68%"
          theme="green"
          delay=".04s"
        />
        <StatCard
          icon="💧"
          value="12,780"
          label="KL · Water Usage"
          tag="↑ 2.1%"
          fillWidth="54%"
          theme="blue"
          delay=".09s"
        />
        <StatCard
          icon="🔥"
          value="1,248"
          label="L · Diesel Consumed"
          tag="↓ 1.8%"
          fillWidth="32%"
          theme="amber"
          delay=".13s"
        />
        <StatCard
          icon="♻️"
          value="24,170"
          label="Kg · Total Waste"
          tag="↓ 3.5%"
          fillWidth="46%"
          theme="rose"
          delay=".17s"
        />
        <DonutPanel
          title="Energy Mix"
          unit="MWh"
          centerValue="49%"
          centerLabel="Power"
          donutData={energyDonutData}
          donutOptions={donutOptions}
          legendItems={[
            { color: '#22c55e', label: 'Solar Gen', value: '25,688' },
            { color: '#fbbf24', label: 'Wind', value: '3 KWh' },
            { color: '#60a5fa', label: 'Grid', value: '322 KWh' },
            { color: '#f87171', label: 'Diesel', value: '4.8 MW' },
          ]}
          dotColor="#22c55e"
          delay=".2s"
        />
        <DonutPanel
          title="Water Split"
          unit="KL"
          centerValue="54%"
          centerLabel="Gen"
          donutData={waterDonutData}
          donutOptions={donutOptions}
          legendItems={[
            { color: '#3b82f6', label: 'Generated', value: '1,920' },
            { color: '#06b6d4', label: 'Raw', value: '1,281' },
            { color: '#10b981', label: 'Soft', value: '271' },
            { color: '#8b5cf6', label: 'RO', value: '92' },
          ]}
          dotColor="#3b82f6"
          delay=".25s"
        />
        <DonutPanel
          title="Waste Types"
          unit="Kg"
          centerValue="41%"
          centerLabel="Wood"
          donutData={wasteDonutData}
          donutOptions={donutOptions}
          legendItems={[
            { color: '#10b981', label: 'Wood', value: '9,920' },
            { color: '#ef4444', label: 'Plastic', value: '5,830' },
            { color: '#06b6d4', label: 'Board', value: '8,420' },
          ]}
          dotColor="#f43f5e"
          delay=".3s"
        />
        <KPIPanel delay=".33s" />
        <AreaPanel areaChartData={areaChartData} areaChartOptions={areaChartOptions} delay=".38s" />
      </div>
    </>
  );
};

const Topbar = ({ currentTime, navItems, activeNav, setActiveNav }) => (
  <header className="topbar">
    <div className="brand">
     <div className="brand-ring" style={{ animation: 'none' }}>
  <img src="/assets/tel.jpeg" alt="brand" />
</div>
      <span className="brand-name">Tel<em> Environment</em></span>
    </div>
    <nav className="topnav">
      {navItems.map(item => (
        <div
          key={item}
          className={`tn ${activeNav === item ? 'on' : ''}`}
          onClick={() => setActiveNav(item)}
        >
          {item}
        </div>
      ))}
    </nav>
    <div className="topbar-r">
      <div className="live-pill"><div className="lp-dot"></div>Live</div>
      <div className="tb-clock">{currentTime}</div>
      <div className="tb-av">AK</div>
    </div>
  </header>
);

export default App;
